/**
 * AuthGate — resolves authentication state before rendering the app.
 * Hydrates from cached profile (patient_me) INSTANTLY so page reloads (F5) render in 0ms,
 * then validates/refreshes user session silently in the background.
 */
import { useEffect, useState, type ReactNode } from 'react';
import UsersAPI from '@/api/users.api';
import { getAuthToken } from '@/api/client';
import { useAuthStore } from '@/stores/auth.store';
import BlockedScreen from '@/features/auth/BlockedScreen';

interface AuthGateProps {
  children: ReactNode;
}

export function AuthGate({ children }: AuthGateProps) {
  const { user, setUser, clearUser } = useAuthStore();
  const [isResolving, setIsResolving] = useState(() => {
    // Synchronous hydration: if token + cached profile exist, resolve INSTANTLY (0 ms wait on F5!)
    const token = getAuthToken();
    if (!token) return false;
    const cached = localStorage.getItem('patient_me');
    if (cached) {
      try {
        const raw = JSON.parse(cached);
        // Unwrap {result: user} if backend wrapped it
        const me = raw?.result ?? raw;
        const userId = me?.id || me?.userId || me?._id || (me as any)?.objectId;
        if (me && userId) {
          const normalized = { ...me, id: userId };
          // Re-save clean version without result wrapper
          localStorage.setItem('patient_me', JSON.stringify(normalized));
          useAuthStore.getState().setUser(normalized);
          return false; // App renders immediately without blocking on network
        }
        // Cached profile is corrupt (no id) — clear it and wait for network
        localStorage.removeItem('patient_me');
      } catch { localStorage.removeItem('patient_me'); }
    }
    return true; // No valid cached profile — wait for network
  });

  useEffect(() => {
    async function resolve() {
      const token = getAuthToken();
      if (!token) {
        clearUser();
        setIsResolving(false);
        return;
      }

      // Silent background validation (updates user or logs out if token is 401)
      try {
        const me = await UsersAPI.getMe();
        if (me) {
          const userId = me.id || (me as any).userId || (me as any)._id || (me as any).objectId;
          if (userId) {
            const normalized = { ...me, id: userId };
            setUser(normalized);
          }
          // If no userId found, keep whatever was hydrated from cache (don't overwrite with broken user)
        } else if (!localStorage.getItem('patient_me')) {
          clearUser();
        }
      } catch {
        // Network error / timeout — if no cached user profile, clear auth
        if (!localStorage.getItem('patient_me')) {
          clearUser();
        }
      } finally {
        setIsResolving(false);
      }
    }

    resolve();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (isResolving) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          background: 'var(--color-bg-secondary, #f9fafb)',
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            border: '3px solid var(--color-border, #e5e7eb)',
            borderTopColor: 'var(--color-primary, #4f46e5)',
            borderRadius: '50%',
            animation: 'spin 0.6s linear infinite',
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Show blocked screen for any suspended account (admin override)
  if (user?.blocked) {
    return <BlockedScreen />;
  }

  return <>{children}</>;
}
